import React, { useMemo } from 'react'
import { type Row, useExpanded, useTable } from 'react-table'
import { Box, Button, Grid, Pagination, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import DeleteIcon from '@mui/icons-material/Delete'
import { type UsFilters } from 'components'
import CircularProgress from '@mui/material/CircularProgress'
export interface TableEntity {
  id?: number | null
  updatedBy?: string | null
  lastUpdated?: string | null
  [key: string]: any
}

export interface TableColumn {
  Header: string
  accessor: string
  order_field: string
}

interface UsTableProps {
  entities: TableEntity[]
  filters: UsFilters
  updateFilters: (filters: any) => void
  triggerReload: () => void
  pageCount: number | null
  totalCount: number | null
  additionalColumns: TableColumn[]
  orderField: string
  loading: boolean
  orderDirection: string
  openEdit: (entity: TableEntity) => void
  openDelete?: (entity: TableEntity) => void
  showDelete?: boolean
}

const tableStyles = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 15,
  '& thead th': {
    backgroundColor: '#efefef',
    fontSize: 17,
    textAlign: 'left',
    padding: 2,
    cursor: 'pointer',
    borderRight: 'solid lightgray 1px',
    borderLeft: 'solid lightgray 1px'
  },
  '& th.noclick-column-header': {
    cursor: 'default'
  },
  '& td': {
    padding: '7px'
  },
  '& tr:nth-of-type(even)': {
    backgroundColor: 'lightgray'
  },
  '& tr:nth-of-type(odd)': {
    borderRight: 'solid lightgray 1px',
    borderLeft: 'solid lightgray 1px'
  }
}

export const UsTable: React.FC<UsTableProps> = ({ entities, updateFilters, triggerReload, filters, pageCount, totalCount, orderField, orderDirection, additionalColumns, loading, openEdit, openDelete, showDelete = false }) => {
  const navigate = useNavigate()

  const columns: any = useMemo(
    () => {
      const headers = [
        { Header: 'ID', accessor: 'id', order_field: 'id' },
        ...additionalColumns,
        {
          Header: 'Edit',
          id: 'edit',
          Cell: ({ row }: { row: Row<TableEntity> }) => (
                    <Button
                        id={`edit-${(row.values.id as string)}`}
                        variant="contained"
                        size='medium'
                        onClick={() => {
                          openEdit(row.original)
                        }}
                    >
                        <ModeEditIcon />
                    </Button>
          )
        }
      ]
      if (showDelete) {
        headers.push({
          Header: 'Delete',
          id: 'delete',
          Cell: ({ row }: { row: Row<TableEntity> }) => (
                      <Button
                          id={`delete-${(row.values.id as string)}`}
                          variant="contained"
                          size='medium'
                          onClick={() => {
                            openDelete?.(row.original)
                          }}
                      >
                          <DeleteIcon />
                      </Button>
          )
        })
      }
      return headers
    },
    [navigate, additionalColumns]
  )

  const tableData = useMemo(() => entities, [entities])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable<TableEntity>(
    {
      columns,
      data: tableData
    },
    useExpanded
  )

  function updateOrdering (field: string, direction: string): void {
    updateFilters({ order_field: field, order_direction: direction })
    triggerReload()
  }

  function handleHeaderClick (column: any): void {
    const direction = orderField === column.order_field ? (orderDirection === 'ASC' ? 'DESC' : 'ASC') : orderDirection
    updateOrdering(column.order_field, direction)
  }

  if (loading) {
    return (<Box display={'flex'} justifyContent={'center'}><CircularProgress/> </Box>)
  }

  if (entities.length === 0) {
    return (<Box display={'flex'} justifyContent={'center'}><Typography>No Results</Typography></Box>)
  }

  const getPaginate = (): React.ReactElement => {
    if (pageCount === null || totalCount === null) {
      return <></>
    }
    return (
    <Box width="100%" display="flex" justifyContent="center" sx={{ mb: 1, mt: 1 }}>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item xs/>
        <Grid item>
          <Pagination page={filters.page} count={pageCount} onChange={(event, page) => {
            updateFilters({ page })
            triggerReload()
          }}
          />
        </Grid>
        <Grid item xs display="flex" justifyContent="flex-end">
          <Typography variant="subtitle2">{totalCount} Total</Typography>
        </Grid>
      </Grid>
    </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      {getPaginate()}
    <Box component="table" {...getTableProps()} sx={tableStyles}>
          <thead>
            {headerGroups.map((headerGroup, index) => {
              return (
                    <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                        {headerGroup.headers.map((column: any) => {
                          const isActionOnly = column.id === 'edit' || column.id === 'delete' || (column.order_field ?? '') === ''
                          return (
                            <th
                                {...column.getHeaderProps}
                                key={column.id}
                                onClick={() => { if (!isActionOnly) handleHeaderClick(column) }}
                                className={isActionOnly ? 'noclick-column-header' : '' }
                            >
                                <div style={{ display: 'flex', flexDirection: 'row' }}>
                                {column.render('Header')}
                                <span>
                                    {orderField === column.order_field ? (orderDirection === 'ASC' ? ' ↓' : ' ↑') : ''}
                                </span>
                                </div>
                            </th>
                          )
                        })}
                    </tr>
              )
            })}
            </thead>
            <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row)
              return (
                    <tr {...row.getRowProps()} key={row.id}>
                        {row.cells.map((cell) => (
                            <td {...cell.getCellProps()} key={cell.column.id}>{cell.render('Cell')}</td>
                        ))}
                    </tr>
              )
            })}
            </tbody>
        </Box>
      {getPaginate()}
    </Box>)
}
